import React, { useState } from "react";

import jsonExport from "jsonexport/dist";
import DateFnsUtils from "@date-io/date-fns";
import { API, graphqlOperation } from "aws-amplify";
import {
  searchInboundTotal,
  searchOutboundTotal,
} from "../../../graphql/report-queries";
import { getProduct } from "../../../graphql/queries";

import {
  Card,
  Grid,
  makeStyles,
  MenuItem,
  TextField as FilterField,
  Typography,
} from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import {
  ArrayField,
  Button,
  Datagrid,
  DateField,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  NumberField,
  required,
  ResourceContextProvider,
  SimpleForm,
  TextField,
  Title,
  TopToolbar,
  useList,
  useListContext,
  useNotify,
} from "react-admin";

import { ExportIcon, LaunchIcon } from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Pagination } from "../../../components/Pagination";
import { AccountDropDown } from "../../../components/common/AccountDropDown";
import { MonthSlider } from "../../../components/common/MonthSlider";
import { logError } from "../../../utils/appUtils";
import { UnitOptions } from "../../../utils/constants";
import { convertUnits, getUnitName } from "../../../utils/loadTypeUtils";
import { deepRedirect, getBackPath } from "../../../utils/utils";
import { createDateRangeParams, YearSelect } from "../ReportsUtils";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    position: "absolute",
    bottom: theme.spacing(2),
  },
  totals: {
    marginLeft: theme.spacing(2),
  },
}));

const formatter = new Intl.NumberFormat("en-US");

/**
 * Sum a shipment's inventory into one bucket per UOM.
 * With a target unit every line converts into that single bucket; with the
 * Units filter left blank nothing is converted and each line keeps its own
 * UOM, falling back to the product's default UOM when the inventory has none.
 */
const sumInventory = (shipment, units, productMap) => {
  const totals = {};
  let convertError = false;

  shipment.inventories.items.forEach((itm) => {
    if (convertError) return;
    const product = productMap[itm.productId];

    if (units) {
      const conversion = convertUnits(
        itm.quantity,
        itm.inventoryUOM,
        units,
        product,
      );
      if (conversion.error) {
        convertError = true;
        return;
      }
      totals[units] = (totals[units] || 0) + conversion.value;
    } else {
      const uom =
        itm.inventoryUOM || product?.defaultUOM || shipment.totalUOM || "";
      totals[uom] = (totals[uom] || 0) + (Number(itm.quantity) || 0);
    }
  });

  if (convertError) return { error: true, subtotals: [] };
  return { error: false, subtotals: toSubtotalList(totals) };
};

const toSubtotalList = (totals) =>
  Object.keys(totals)
    .sort()
    .map((uom) => ({ uom, value: totals[uom] }));

const unitLabel = (uom) => getUnitName(uom) || uom;

const formatSubtotals = (record) =>
  record.subtotal === "MISSING"
    ? "MISSING"
    : (record.subtotals || [])
        .map(({ value }) => formatter.format(value))
        .join(" / ");

const formatSubtotalUnits = (record) =>
  record.subtotal === "MISSING"
    ? ""
    : (record.subtotals || []).map(({ uom }) => unitLabel(uom)).join(" / ");

const emptySubtotalCells = { subtotal: "", UOM: "" };

/**
 * The export carries the raw subtotal number and the raw UOM code, never the
 * grouped display string: a thousands-separated "12,500" or a "1,200 / 45"
 * pair is text to a spreadsheet, and the Subtotal column stops summing. A
 * mixed-UOM shipment therefore spreads one bucket per row down its own block
 * (there is always at least one inventory row per bucket), so every cell in
 * the column stays a number beside its own unit.
 */
const exportSubtotalCells = (shipment, index) => {
  if (shipment.subtotal === "MISSING") {
    return index === 0 ? { subtotal: "MISSING", UOM: "" } : emptySubtotalCells;
  }
  const bucket = (shipment.subtotals || [])[index];
  return bucket
    ? { subtotal: bucket.value, UOM: bucket.uom }
    : emptySubtotalCells;
};

const exporter = (finalData) => {
  const shipmentsForExport = [];

  finalData.forEach((shipment) => {
    const inventories = shipment.inventories?.items || [];

    if (inventories.length === 0) {
      // Row with shipment info and no inventory
      shipmentsForExport.push({
        accountName: shipment.accountName,
        shipmentId: shipment.id,
        shipmentType: shipment.shipmentType,
        shipmentDate: new Date(shipment.shipmentDate).toLocaleDateString(),
        poNumber: shipment.poNumber,
        ...exportSubtotalCells(shipment, 0),
        productName: "",
        productCode: "",
        quantity: "",
        inventoryUOM: "",
      });
    } else {
      inventories.forEach((inv, index) => {
        shipmentsForExport.push({
          accountName: shipment.accountName,
          shipmentId: shipment.id,
          shipmentType: shipment.shipmentType,
          shipmentDate: new Date(shipment.shipmentDate).toLocaleDateString(),
          poNumber: shipment.poNumber,
          ...exportSubtotalCells(shipment, index),
          productName: inv.productName,
          productCode: inv.productCode,
          quantity: inv.quantity,
          inventoryUOM: inv.inventoryUOM,
        });
      });
    }
  });

  jsonExport(
    shipmentsForExport,
    {
      rename: [
        // Customize headers if needed
        // "Shipment ID", "Date", "PO #", "Product", etc.
      ],
    },
    (err, csv) => {
      downloadCSV(
        csv,
        `report_in_out_totals_${new Date().toLocaleDateString()}`,
      );
    },
  );
};

const ListActions = (props) => {
  const classes = useStyles();
  const validateRequired = required();
  const {
    finalData,
    accountId,
    setAccountId,
    loadTypes,
    loadType,
    setLoadType,
    units,
    setUnits,
    year,
    setYear,
    monthRange,
    setMontRange,
    searchTerm,
    setSearchTerm,
    getInOutTotalsData,
  } = props;

  return (
    <TopToolbar>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Grid container direction="row" spacing={1}>
          <Grid item container xs={10} spacing={1}>
            <Grid item xs={12} md={4}>
              <AccountDropDown
                source="accountId"
                label="Account"
                onAccountChange={(value) => setAccountId(value?.id)}
                validate={validateRequired}
                allowEmpty={false}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <YearSelect
                year={year}
                setYear={setYear}
                validate={validateRequired}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <MonthSlider
                maxMonths={accountId ? 6 : 3}
                monthRange={monthRange}
                setMontRange={setMontRange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FilterField
                select
                id="units"
                label="Units"
                variant="outlined"
                margin="dense"
                fullWidth
                alwaysOn
                value={units}
                onChange={(evt) => setUnits(evt?.target?.value)}
              >
                {/* Blank: no conversion, every line keeps its own UOM. */}
                <MenuItem value="">
                  <span>&nbsp;</span>
                </MenuItem>
                {UnitOptions.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {" "}
                    {unit.name}{" "}
                  </MenuItem>
                ))}
              </FilterField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FilterField
                select
                id="loadType"
                label="Load Type"
                variant="outlined"
                margin="dense"
                fullWidth
                alwaysOn
                value={loadType}
                onChange={(evt) => setLoadType(evt?.target?.value)}
              >
                {loadTypes.map((load) => (
                  <MenuItem key={load.id} value={load.id}>
                    {" "}
                    {load.id ? load.commodity?.name : <span>&nbsp;</span>}{" "}
                  </MenuItem>
                ))}
              </FilterField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FilterField
                id="search"
                label="Product Name / Code"
                variant="outlined"
                margin="dense"
                fullWidth
                alwaysOn
                value={searchTerm}
                onChange={(event) => setSearchTerm(event?.target?.value)}
              />
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <Button
              label="Run"
              variant="contained"
              color="default"
              alwaysOn
              className={classes.submitButton}
              onClick={getInOutTotalsData}
              disabled={!year}
            />
          </Grid>
        </Grid>
      </MuiPickersUtilsProvider>

      <Button
        label="Export"
        disabled={finalData?.length === 0}
        onClick={() => exporter(finalData)}
      >
        <ExportIcon />
      </Button>
      <BackButton basePath="/reports" />
    </TopToolbar>
  );
};

// /reportTotalInOut
export const TotalInOutReport = (props) => {
  const notify = useNotify();
  const [loadTypes] = useState([
    { id: null, name: null },
    ...window.appUser.reloadYard.activeYardCommodities.items,
  ]);

  const [accountId, setAccountId] = useState("");
  const [loadType, setLoadType] = useState("");
  const [units, setUnits] = useState("");
  const [year, setYear] = useState();
  const [monthRange, setMontRange] = useState([1, 1]);
  const [searchTerm, setSearchTerm] = useState("");

  // One { uom, value } entry per UOM present in the result set.
  const [inboundTotals, setInboundTotals] = useState([]);
  const [outboundTotals, setOutboundTotals] = useState([]);

  const [finalData, setFinalData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getInOutTotalsData = async () => {
    try {
      setLoading(true);

      const normalizedSearchTerm = searchTerm?.toLowerCase().trim();

      const inboundRunningTotals = {};
      const outboundRunningTotals = {};
      const inbounds = [];
      const outbounds = [];
      const productMap = {};
      const productIds = new Set();
      let needsDefaultUOM = false;
      let globalError = false;
      const { startDate, endDate } = createDateRangeParams(year, monthRange);
      const limit = 1000;

      const inboundSearchInput = {
        sort: { field: "placedDateTime", direction: "asc" },
        filter: {
          and: [
            { placedDateTime: { gte: `${startDate}` } },
            { placedDateTime: { lt: `${endDate}` } },
          ],
        },
        limit,
      };
      const outboundSearchInput = {
        sort: { field: "departureDateTime", direction: "asc" },
        filter: {
          and: [
            { departureDateTime: { gte: `${startDate}` } },
            { departureDateTime: { lte: `${endDate}` } },
          ],
        },
        limit,
      };
      if (accountId) {
        inboundSearchInput.filter.accountId = { eq: accountId };
        outboundSearchInput.filter.accountId = { eq: accountId };
      } else {
        inboundSearchInput.filter.reloadYardId = {
          eq: window.appUser.reloadYardId,
        };
        outboundSearchInput.filter.reloadYardId = {
          eq: window.appUser.reloadYardId,
        };
      }
      if (loadType) {
        inboundSearchInput.filter.reloadYardCommodityId = { eq: loadType };
        outboundSearchInput.filter.reloadYardCommodityId = { eq: loadType };
      }

      let nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true
            ? inboundSearchInput
            : { ...inboundSearchInput, nextToken };
        const result = await API.graphql(
          graphqlOperation(searchInboundTotal, input),
        );
        inbounds.splice(-1, 0, ...result.data.searchInboundShipments.items);
        if (result.data.searchInboundShipments.items.length < limit) break;
        nextToken = result.data.searchInboundShipments.nextToken;
      }
      nextToken = true;
      while (nextToken) {
        const input =
          nextToken === true
            ? outboundSearchInput
            : { ...outboundSearchInput, nextToken };
        const result = await API.graphql(
          graphqlOperation(searchOutboundTotal, input),
        );
        outbounds.splice(-1, 0, ...result.data.searchOutboundShipments.items);
        if (result.data.searchOutboundShipments.items.length < limit) break;
        nextToken = result.data.searchOutboundShipments.nextToken;
      }

      inbounds.forEach((inbound) => {
        const filteredItems = [];

        inbound.inventories.items.forEach((itm) => {
          const matchesSearch =
            !normalizedSearchTerm ||
            itm.productName?.toLowerCase().includes(normalizedSearchTerm) ||
            itm.productCode?.toLowerCase().includes(normalizedSearchTerm);

          const matchesLoadType =
            !loadType || itm.reloadYardCommodityId === loadType;

          if (matchesSearch && matchesLoadType) {
            if (itm.productId) {
              productIds.add(itm.productId);
              if (!itm.inventoryUOM) needsDefaultUOM = true;
            }
            filteredItems.push({
              ...itm,
              quantity: itm.quantityAdvised,
            });
          }
        });

        inbound.inventories.items = filteredItems;
      });

      outbounds.forEach((outbound) => {
        const filteredItems = [];

        outbound.shipmentLineItems.items.forEach((itm) => {
          const inv = itm.inventory;
          if (!inv) return;

          const matchesSearch =
            !normalizedSearchTerm ||
            inv.productName?.toLowerCase().includes(normalizedSearchTerm) ||
            inv.productCode?.toLowerCase().includes(normalizedSearchTerm);

          const matchesLoadType =
            !loadType || inv.reloadYardCommodityId === loadType;

          if (matchesSearch && matchesLoadType) {
            if (inv.productId) {
              productIds.add(inv.productId);
              if (!inv.inventoryUOM) needsDefaultUOM = true;
            }
            filteredItems.push({
              id: inv.id,
              productName: inv.productName,
              productCode: inv.productCode,
              inventoryUOM: inv.inventoryUOM,
              quantity: itm.totalQuantityToPick,
              productId: inv.productId,
              reloadYardCommodityId: inv.reloadYardCommodityId,
            });
          }
        });

        outbound.inventories = { items: filteredItems };
      });

      // Products are only needed to convert into a target unit, or to supply a
      // default UOM to a row that carries none. A raw-UOM report over rows that
      // all have their own UOM therefore skips the lookups entirely. Results are
      // paired back by id because a deleted or unreadable product resolves to
      // null rather than to an error, and reading an id off it would take the
      // whole report down.
      if (units || needsDefaultUOM) {
        const fetchIds = [...productIds];
        const response = await Promise.all(
          fetchIds.map((productId) =>
            API.graphql(graphqlOperation(getProduct, { id: productId })),
          ),
        );
        fetchIds.forEach((productId, index) => {
          productMap[productId] = response[index]?.data?.getProduct || null;
        });
      }
      const applySubtotals = (shipment, runningTotals) => {
        const { error, subtotals } = sumInventory(shipment, units, productMap);

        if (error) {
          if (!globalError)
            notify(
              "Inventory not linked to Product or Product missing unit conversion rate.",
              "warning",
            );
          globalError = true;
          shipment.subtotal = "MISSING";
          shipment.subtotals = [];
        } else {
          shipment.subtotals = subtotals;
          // Only meaningful as a number while the shipment sits in one UOM;
          // mixed-UOM rows render from subtotals instead.
          shipment.subtotal =
            subtotals.length === 1 ? subtotals[0].value : null;
          subtotals.forEach(({ uom, value }) => {
            runningTotals[uom] = (runningTotals[uom] || 0) + value;
          });
        }
      };

      inbounds.forEach((inbound) => {
        applySubtotals(inbound, inboundRunningTotals);
        inbound.shipmentDate = inbound.placedDateTime;
        inbound.shipmentType = "Inbound";
      });

      outbounds.forEach((outbound) => {
        applySubtotals(outbound, outboundRunningTotals);
        outbound.shipmentDate = outbound.departureDateTime;
        outbound.shipmentType = "Outbound";
      });

      let totalData = inbounds.concat(outbounds);
      totalData = totalData.filter(
        (itm) =>
          itm.subtotal === "MISSING" ||
          itm.subtotals.some(({ value }) => value !== 0),
      );

      if (!globalError) {
        setInboundTotals(toSubtotalList(inboundRunningTotals));
        setOutboundTotals(toSubtotalList(outboundRunningTotals));
      } else {
        setInboundTotals([]);
        setOutboundTotals([]);
      }
      setFinalData(totalData);
    } catch (err) {
      logError("TotalInOutLoads:getInOutTotalsData", err, notify);
      setFinalData([]);
    } finally {
      setLoading(false);
    }
  };

  const fixedFilter = ""; // {"lowInventoryAlert": true};
  const defaultSort = { field: "shipmentDate", order: "ASC" };
  const defaultPerPage = 25;

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
  });

  // console.log("finalData", finalData);

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Report In/Out Totals" />} />
        <ListActions
          {...props}
          finalData={finalData}
          accountId={accountId}
          setAccountId={setAccountId}
          loadTypes={loadTypes}
          loadType={loadType}
          setLoadType={setLoadType}
          units={units}
          setUnits={setUnits}
          year={year}
          setYear={setYear}
          monthRange={monthRange}
          setMontRange={setMontRange}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          getInOutTotalsData={getInOutTotalsData}
        />
        <Card>
          <ReportGrid
            accountId={accountId}
            history={props.history}
            backPath={getBackPath({ ...props, basePath: "/reports" })}
          />
          <PaginationToolbar
            inboundTotals={inboundTotals}
            outboundTotals={outboundTotals}
          />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const ReportGrid = ({ accountId, history, backPath, ...props }) => {
  const listContext = useListContext(props);
  listContext.basePath = "/inboundShipments";
  listContext.resource = "inboundShipments";

  return (
    <Datagrid rowClick="expand" expand={<InventoryPeak />} {...listContext}>
      {!accountId && <TextField source="accountName" label="Account" />}
      <DateField source="shipmentDate" label="Date" />
      <TextField source="shipmentType" label="Shipment" />
      <TextField source="poNumber" label="PO Number" />
      <FunctionField
        source="subtotal"
        label="Subtotal"
        render={formatSubtotals}
      />
      <FunctionField
        source="totalUOM"
        label="UOM"
        render={formatSubtotalUnits}
      />
      {/* deepRedirect rather than a plain link so the shipment's Back button
          returns to this report instead of the shipment list. The click is
          stopped from bubbling so it doesn't also toggle the row's expand. */}
      <FunctionField
        label=""
        render={(record) => (
          <Button
            label="Open"
            onClick={(event) => {
              event.stopPropagation();
              deepRedirect(
                history,
                record.shipmentType === "Outbound"
                  ? `/outboundShipments/${record.id}`
                  : `/inboundShipments/${record.id}`,
                backPath,
                {},
              );
            }}
          >
            <LaunchIcon />
          </Button>
        )}
      />
    </Datagrid>
  );
};

const TotalsLine = ({ label, totals, className }) => (
  <Typography className={className}>
    {totals.length > 0 &&
      `${label}: ${totals
        .map(({ uom, value }) => `${formatter.format(value)} ${unitLabel(uom)}`)
        .join(", ")}`}
  </Typography>
);

const PaginationToolbar = ({ inboundTotals, outboundTotals, ...props }) => {
  const classes = useStyles();
  const listContext = useListContext(props);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <TotalsLine
          label="Inbound"
          totals={inboundTotals}
          className={classes.totals}
        />
        <TotalsLine
          label="Outbound"
          totals={outboundTotals}
          className={classes.totals}
        />
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const InventoryPeak = ({ record, ...props }) => {
  if (!record || !record.inventories?.items?.length) return null;

  return (
    <ResourceContextProvider value="inventories">
      <Grid container>
        <Grid item xs={false} sm={1}>
          <></>
        </Grid>
        <Grid item xs={12} sm={10}>
          <ArrayField source="inventories.items" record={record} fullWidth>
            <Datagrid>
              <TextField source="productName" label="Product Name" />
              <TextField source="productCode" label="Product Code" />
              <NumberField source="quantity" label="Quantity" />
              <TextField source="inventoryUOM" label="UOM" />
            </Datagrid>
          </ArrayField>
        </Grid>
        <Grid item xs={false} sm={3}>
          <></>
        </Grid>
      </Grid>
    </ResourceContextProvider>
  );
};
